import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock-upgrade';
import * as focusTrap from 'focus-trap';
import { gsap, Power2 } from 'gsap/all';
import InlineSearch from '@pratt/js/components/algolia/inline-search';

class NavSearchModal {
  constructor() {
    this.rootEl = document.querySelector('.js-nav-search');
    this.body = document.querySelector('body');
    this.tlClose = gsap.timeline();
    this.tlOpen = gsap.timeline();

    if (!this.rootEl) {
      throw new Error('Root nav search element not found.');
    }

    this.triggerEl = document.querySelector('.js-nav-search-trigger');
    if (!this.triggerEl) {
      throw new Error('Trigger nav search element not found.');
    }

    this.closeButtonEl = this.rootEl.querySelector('.js-nav-search-close');
    if (!this.closeButtonEl) {
      throw new Error('Close button nav search element not found.');
    }

    this.isOpen = false;
    this.focusTrap = focusTrap.createFocusTrap(this.rootEl, {
      escapeDeactivates: false,
      initialFocus: () => this.rootEl.querySelector('input'),
      setReturnFocus: this.triggerEl,
      allowOutsideClick: true,
    });

    this.search = new InlineSearch('.js-nav-search', '.js-nav-search-input', '.js-nav-search-hits');

    this.init();
  }

  init() {
    // Toggle search on trigger button click
    this.triggerEl.addEventListener('click', () => {
      this.isOpen = !this.isOpen;
      this.update();
    });

    // Close the search on close button click
    this.closeButtonEl.addEventListener('click', () => {
      this.isOpen = false;
      this.update();
    });

    // Close the search on escape
    document.addEventListener('keydown', evt => {
      if (this.isOpen && evt.key === 'Escape') {
        evt.preventDefault();
        this.isOpen = false;
        this.update();
      }
    });

    // Close the search on mobile breakpoint
    window.matchMedia('(min-width: 768px)').addEventListener('change', query => {
      if (!query.matches) {
        this.isOpen = false;
        this.update();
      }
    });
  }

  close(returnFocus = true) {
    if (this.isOpen) {
      this.isOpen = false;
      this.update(returnFocus);
    }
  }

  openAnimation() {
    this.tlOpen.to(this.rootEl, {
      autoAlpha: 1,
      duration: 0.2,
      ease: Power2.easeInOut,
    });

    this.tlOpen.to(this.rootEl.querySelector('.nav-search__line line'), {
      strokeDashoffset: 0,
      duration: 0.3,
      ease: Power2.easeInOut,
    });

    this.tlOpen.to(
      this.rootEl.querySelector('.nav-search__content'),
      {
        opacity: 1,
        duration: 0.2,
        ease: Power2.easeInOut,
        onComplete: () => {
          this.focusTrap.activate();
        },
      },
      '-=0.15',
    );
  }

  closeAnimation() {
    this.tlClose.to('.nav-search__line line, .nav-search__content', {
      opacity: 0,
      duration: 0.2,
      ease: Power2.easeInOut,
    });

    this.tlClose.to(this.rootEl, {
      autoAlpha: 0,
      duration: 0.2,
      ease: Power2.easeInOut,
      onComplete: () => {
        gsap.set('.nav-search__line line, .nav-search__content', {
          clearProps: true,
        });
      },
    });
  }

  update(returnFocus = true) {
    this.triggerEl.setAttribute('aria-expanded', this.isOpen);
    this.rootEl.setAttribute('aria-hidden', !this.isOpen);

    if (this.isOpen) {
      disableBodyScroll(this.rootEl);
      this.openAnimation();
    } else {
      enableBodyScroll(this.rootEl);
      this.closeAnimation();
      this.focusTrap.deactivate({ returnFocus });
    }
  }
}

export default NavSearchModal;
